import React from 'react';
import Box from '@mui/material/Box';

import type { Props as IconProps } from 'src/components/ui/Icon';
import Icon from 'src/components/ui/Icon';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import type { NewsCategory, Ad, Post, User } from 'src/types/__generated__/graphql';
import type { MediumForThumbnail } from 'src/utils/getMediaThumbnail';
import { styledComponent } from 'src/utils/styled';
import TinyPreviewCardMedia from 'src/components/ui/TinyPreviewCard/TinyPreviewCardMedia';
import TinyPreviewCardContent from 'src/components/ui/TinyPreviewCard/TinyPreviewCardContent';

const StyledBox = styledComponent(Box)<{
    $hasBoxShadow: boolean;
}>`
    display: flex;
    flex-direction: row;
    border-radius: 0.5rem;
    position: relative;
    width: 100%;
    box-shadow: ${({ $hasBoxShadow }) =>
        $hasBoxShadow ? `0rem 0.25rem 2rem -0.5rem ${colours.blackOpacity.O16}` : undefined};
`;

const StyledIcon = styledComponent(Icon)`
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    z-index: 10;
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        top: 1rem;
    }
`;

export interface TinyPreviewCardProps {
    media?: MediumForThumbnail[];
    mediaUrl?: string;
    type: 'ad' | 'link' | 'newsPost' | 'project' | 'company' | 'pagesJaunesPro' | 'post' | 'user';
    ad?: Pick<Ad, 'bedroomNumber' | 'roomNumber' | 'livingArea' | 'address' | 'surfaceArea'>;
    post?: Pick<Post, 'owner' | 'createdAt' | 'message' | 'media'>;
    user?: Pick<User, 'mutualContactsCount' | 'fullName' | 'initials'>;
    iconName?: string;
    title?: string;
    subtitle?: string | null;
    tag?: Pick<NewsCategory, 'nameEn' | 'nameFr' | 'icon'> | null;
    onClick?: () => void;
    hasBoxShadow?: boolean;
}

const TinyPreviewCard: React.FC<TinyPreviewCardProps> = ({
    media,
    mediaUrl,
    type,
    title,
    tag,
    ad,
    iconName,
    post,
    subtitle,
    user,
    onClick,
    hasBoxShadow = true,
    ...rest
}) => (
    <StyledBox
        onClick={onClick}
        sx={onClick ? { cursor: 'pointer' } : undefined}
        data-target-id={`tinyPreviewCard-${type}`}
        $hasBoxShadow={hasBoxShadow}
        {...rest}
    >
        <TinyPreviewCardMedia type={type} media={media} mediaUrl={mediaUrl} fallbackText={user?.initials} />
        <TinyPreviewCardContent
            type={type}
            ad={ad}
            post={post}
            user={user}
            title={title}
            subtitle={subtitle}
            tag={tag}
        />
        {iconName && <StyledIcon size="1.5rem" name={iconName as IconProps['name']} containerSize="0" />}
    </StyledBox>
);

export default TinyPreviewCard;
